import { useId, lazy, Suspense, useCallback, useMemo, useReducer, useRef, useState } from "react";
import { kindsText } from "../inputCards/typeWords";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { WorkflowGraphDensity } from "../../../api/userPreferencesApi";
import { reportUiError, toUiError } from "../../../errors/uiError";
import AppButton from "../../../ui/buttons/AppButton";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import ModalShell from "../../../ui/overlays/ModalShell";
import { shortReference } from "../../../shared/references/shortReferences";
import { editIntent, useHerePath, useThreadPreparation } from "../../threads/prepare";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import AddToChangeSet from "../../processAuthoring/draft/AddToChangeSet";
import { ChangeSetStepContext, COMMIT_ONLY } from "../../processAuthoring/draft/changeSetStep";
import type { AuthoringOptions } from "../../processAuthoring/draft/authoringOptions";
import { formLanguages, incompleteLanguages, translatedText } from "../../processAuthoring/draft/bilingualTexts";
import DefinitionPanel from "../../processAuthoring/draft/DefinitionPanel";
import ItemTextFields from "../../processAuthoring/draft/ItemTextFields";
import { DerivationRootContext, derivationOf, itemEditAt, useDerivationRoot } from "../../processAuthoring/draft/itemEdits";
import { useUnsavedDraft } from "../../processAuthoring/draft/useUnsavedDraft";
import { useDefinitionCheck, type DefinitionCheck } from "../../processAuthoring/draft/useDefinitionCheck";
import StepForm, { type StepBase } from "../../processAuthoring/steps/StepForm";
import type { LoadedProcess } from "../data/processCatalogue";
import { carrying, carrySession, openedSession, placementsOf, type Placements } from "../model/carriedCopies";
import { workflowChanges } from "../model/workflowChanges";
import { referenceOf, settledInputs, workflowDraft, type Binding, type WorkflowDraftAction, type WorkflowRole } from "../model/workflowDraft";
import { bindRefusal, inputsAccepting, placementPorts } from "../model/workflowPorts";
import { editorProblems } from "./editorProblems";
import ExportsPanel from "./ExportsPanel";
import NodeInspector from "./NodeInspector";
import type { PaletteItem } from "./paletteDrag";
import { useEditorCatalogue } from "./useEditorCatalogue";
import WorkflowChangeList from "./WorkflowChangeList";
import { useCarriedItem } from "./useCarriedItem";
import StepPicker from "./StepPicker";

// React Flow loads with the first graph shown, not with the app.
const EditGraph = lazy(() => import("./EditGraph"));

export interface WorkflowBase {
  readonly reference: string;
  readonly itemId: string;
  readonly content: WorkflowContent;
}

interface WorkflowEditorProps {
  projectId: string;
  base: WorkflowBase;
  process: LoadedProcess;
  options: AuthoringOptions;
  /** How much the graph's cards show: the density its embedding resolved (F76). */
  density: WorkflowGraphDensity;
  /** Leaves edit mode on the Workflow page. Without it there is nothing to close: embedded
   * in a person step, the step owns finishing the edit (OW-11). */
  onClose?: () => void;
  /** A step or sub-workflow version to add as the editor opens ("Use in a Workflow"). */
  carry?: string | null;
}

type StepDrawer = { readonly forKey: string | null; readonly base: StepBase | null };
type CheckedDraft = Extract<DefinitionCheck, { readonly status: "checked" }>;

/**
 * Edit mode of the Workflow view (authoring-ui.md AU-6, mockup `workflow-editor.html`): a
 * draft of the next version, changed by the step picker, ports and the inspector, checked by the
 * server as it changes, and added to a change set like every other draft.
 */
export default function WorkflowEditor({ projectId, base, process, options, density, onClose, carry = null }: WorkflowEditorProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const aboutId = useId();
  const [draft, setDraft] = useState<WorkflowContent>(base.content);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [drawer, setDrawer] = useState<StepDrawer | null>(null);
  /** What this session carries into the Workflow's saves: the drawer's copies and what the last save sent (D-8b). */
  const [copySession, dispatchCarry] = useReducer(carrySession, base.content, openedSession);
  const sending = useRef<Placements>(copySession.submitted);
  const preparation = useThreadPreparation();
  const here = useHerePath();
  /** The change set a step made in the step form went into: the Workflow is suggested there too. */
  const [stepChangeSetId, setStepChangeSetId] = useState<string | null>(null);
  /** The change set this editing session last saved into, the Workflow or a step: the drawer starts there, so its copies stay where the Workflow's save carries them (D-8b). */
  const [sessionChangeSetId, setSessionChangeSetId] = useState<string | null>(null);
  const { dirty, connectSaver, markSaved, requestClose } = useUnsavedDraft(draft);
  const drawerCloseRef = useRef<{ requestClose: (close: () => void) => void }>(null);
  const stepCatalogue = options.catalogue.status === "loaded" ? options.catalogue.value : null;
  const { catalogue, ports, pending } = useEditorCatalogue(draft, process.catalogue, stepCatalogue);
  const check = useDefinitionCheck(projectId, draft, base.itemId);
  // While the next check runs, the graph's marks and the inspector keep the last answer, so they don't blink at each keystroke.
  const [lastChecked, setLastChecked] = useState<CheckedDraft | null>(null);
  if (check.status === "checked" && check !== lastChecked) {
    setLastChecked(check);
  }
  const known = check.status === "checked" ? check : check.status === "checking" ? lastChecked : null;
  const changes = useMemo(() => workflowChanges(base.content, draft), [base.content, draft]);
  const problems = useMemo(() => editorProblems(draft, check.status === "checked" ? check.problems : []), [draft, check]);
  const problemCounts = useMemo(() => new Map([...problems.byKey].map(([key, messages]) => [key, messages.length])), [problems]);
  const changedKeys = useMemo(() => new Set(changes.flatMap((change) => ("key" in change && change.kind !== "stepRemoved" && !change.kind.startsWith("binding") ? [change.key] : []))), [changes]);

  const dispatch = useCallback((action: WorkflowDraftAction) => {
    setDraft((current) => settledInputs(workflowDraft(current, action), base.content));
  }, [base.content]);

  const bind = useCallback(
    (key: string, slot: string, from: Binding) => {
      const refusal = bindRefusal(draft, ports, key, slot, from);
      if (refusal === null) {
        dispatch({ type: "bind", key, slot, from });
        setMessage(t("workflows.edit.bound", { target: `${key}.${slot}`, source: `${from.from_step}.${from.part}` }));
        return;
      }
      const source = draft.steps.find((step) => step.key === from.from_step);
      const kinds = source === undefined ? [] : (placementPorts(ports, source)?.outputs?.[from.part] ?? []);
      const others = inputsAccepting(draft, ports, kinds).map((input) => `${input.key}.${input.slot}`);
      setMessage(t(`workflows.edit.refusals.${refusal}`, { target: `${key}.${slot}`, source: `${from.from_step}.${from.part}`, kinds: kindsText(kinds, t), others: others.length === 0 ? t("workflows.edit.noOtherInputs") : others.join(", ") }));
    },
    [draft, ports, dispatch, t],
  );

  const canBind = useCallback((key: string, slot: string, from: Binding) => bindRefusal(draft, ports, key, slot, from) === null, [draft, ports]);

  const move = useCallback(
    (key: string, before: string | null) => {
      dispatch({ type: "move", key, before });
    },
    [dispatch],
  );

  const add = useCallback(
    (item: PaletteItem, role: WorkflowRole) => {
      dispatch({ type: "add", use: { kind: item.kind, reference: item.reference }, role, name: item.name });
      setMessage(t("workflows.edit.added", { name: item.name, role: t(`workflows.edit.roles.${role}`) }));
    },
    [dispatch, t],
  );

  const invalidCarriedItem = useCallback(() => { setMessage(t("authoring.similar.invalidUse")); }, [t]);
  useCarriedItem(carry, base.itemId, add, invalidCarriedItem);

  const openStepForm = useCallback((forKey: string | null, reference: string | null) => {
    if (reference === null) {
      setDrawer({ forKey, base: null });
      return;
    }
    readAuthoredItem(reference).then(
      (item) => {
        if (item.content?.kind === "step") {
          setDrawer({ forKey, base: { reference: item.reference, itemId: item.itemId, definition: item.content.definition } });
        }
      },
      (caught: unknown) => {
        reportUiError(toUiError(caught));
      },
    );
  }, []);

  const stepAdded = async (addedTo: string, reference: string | null) => {
    setStepChangeSetId(addedTo);
    setSessionChangeSetId(addedTo);
    const edited = drawer !== null && drawer.forKey !== null ? (drawer.base?.reference ?? null) : null;
    setDrawer(null);
    if (reference === null) {
      return;
    }
    if (edited !== null) {
      // Every placement of the edited version takes the saved one (D-8); the Workflow's own save carries a copy deeper (D-8b).
      const keys = draft.steps.flatMap((step) => (referenceOf(step) === edited ? [step.key] : []));
      dispatch({ type: "repin", from: edited, to: reference });
      dispatchCarry({ type: "stepSaved", edited, saved: reference });
      setMessage(t("workflows.edit.repinned", { keys: keys.join(", "), count: keys.length }));
      return;
    }
    await readAuthoredItem(reference).then((item) => {
      add({ kind: "step", reference, name: item.name }, "main");
    });
  };

  // Derived within the Run's Workflow where the person chose Derive; a Workflow this project doesn't hold is derived as the root itself.
  const chosenWithin = useDerivationRoot();
  const [derivation] = useState(() => derivationOf(chosenWithin, base.reference));
  const commits = useCallback((): readonly CommitBuilder[] => {
    sending.current = placementsOf(draft);
    return [async (head) => [carrying(await itemEditAt(projectId, head, { itemId: base.itemId, base: base.reference, derivation }, draft), copySession)]];
  }, [projectId, base, draft, derivation, copySession]);
  const workflowAdded = (changeSetId: string) => {
    setSessionChangeSetId(changeSetId);
    dispatchCarry({ type: "workflowSaved", submitted: sending.current });
  };

  return (
    <div className="app-workflow-editor space-y-3">
      <div className="app-card flex flex-wrap items-center gap-3 p-2 text-sm" role="toolbar" aria-label={t("workflows.edit.toolbar")}>
        <span className="min-w-[12rem] flex-1">
          {[
            t("workflows.edit.status", { version: shortReference("version", base.reference) }),
            t("authoring.definition.changes", { count: changes.length }),
            t("workflows.edit.problems", { count: problems.count }),
          ].join(" · ")}
          {dirty ? ` · ${t("workflows.edit.notSaved")}` : sessionChangeSetId === null ? null : ` · ${t("workflows.edit.saved")}`}
        </span>
        {pending > 0 ? <LoadingIndicator label={t("workflows.edit.reading")} /> : null}
        <AppButton variant="ghost" size="sm" disabled={changes.length === 0} onClick={() => { setDraft(base.content); dispatchCarry({ type: "discarded", opened: base.content }); setSelectedKey(null); setMessage(t("workflows.edit.discarded")); }}>
          {t("workflows.edit.discard")}
        </AppButton>
        {onClose === undefined ? null : (
          <AppButton variant="secondary" size="sm" onClick={() => { requestClose(onClose, true); }}>{t("workflows.edit.close")}</AppButton>
        )}
      </div>
      <p className="app-text-muted min-h-[1.25rem] text-xs" role="status" aria-live="polite">{message}</p>
      <Suspense fallback={<LoadingIndicator label={t("workflows.view.loading")} className="py-10" />}>
        <EditGraph
          draft={draft}
          reference={base.reference}
          catalogue={catalogue}
          density={density}
          selectedKey={selectedKey}
          problemsByKey={problemCounts}
          changedKeys={changedKeys}
          plans={known?.plans ?? null}
          inputFills={known?.inputFills ?? null}
          onSelect={setSelectedKey}
          onBind={bind}
          canBind={canBind}
          onMove={move}
          onDropItem={add}
        />
      </Suspense>
      <div className="app-workflow-editor-columns">
        <StepPicker projectId={projectId} selfItemId={base.itemId} onAdd={add} onNewStep={() => { openStepForm(null, null); }} />
        {selectedKey === null ? (
          <p className="app-card app-text-muted p-3 text-xs">{t("workflows.edit.selectHint")}</p>
        ) : (
          <NodeInspector
            projectId={projectId}
            draft={draft}
            stepKey={selectedKey}
            plan={known?.plans?.get(selectedKey) ?? null}
            declaredInputs={known?.declaredInputs ?? null}
            catalogue={catalogue}
            ports={ports}
            problems={problems.byKey.get(selectedKey) ?? []}
            refusedBindings={problems.refusedBindings}
            dispatch={dispatch}
            onEditStep={(reference) => { openStepForm(selectedKey, reference); }}
            onAskAgent={(reference, name, kind) => { preparation.prepare(editIntent(projectId, { itemKind: kind, name, reference }, here)); }}
          />
        )}
        <div className="space-y-3">
          <section className="app-card space-y-2 p-3" aria-labelledby={aboutId}>
            <h3 id={aboutId} className="app-title text-sm font-semibold">{t("workflows.edit.about")}</h3>
            <ItemTextFields item={draft} nameLabel={t("workflows.edit.aboutName")} descriptionLabel={t("workflows.edit.aboutDescription")} check={check} onChange={setDraft} />
          </section>
          <ExportsPanel draft={draft} ports={ports} dispatch={dispatch} />
          <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
            <h3 id={headingId} className="app-title text-sm font-semibold">{t("workflows.edit.changes")}</h3>
            <WorkflowChangeList changes={changes} before={base.content} after={draft} catalogue={catalogue} />
            {problems.general.length > 0 ? <ul className="app-error-text space-y-0.5 text-xs">{problems.general.map((problem) => <li key={problem}>{problem}</li>)}</ul> : null}
          </section>
        </div>
      </div>
      <div className="app-workflow-editor-pair">
        <DefinitionPanel content={draft} base={base.content} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          key={stepChangeSetId ?? "new"}
          projectId={projectId}
          defaultTitle={t("workflows.edit.changeSetTitle", { name: draft.name })}
          commits={commits}
          blockedBy={changes.length === 0 ? t("workflows.edit.nothingToAdd") : incompleteLanguages(draft.translations).length > 0 ? t("authoring.bilingual.blocked") : null}
          onSaved={markSaved}
          onAdded={workflowAdded}
          initialChangeSetId={stepChangeSetId}
          notice={<p className="app-help-text text-xs">{t(problems.count > 0 ? "workflows.edit.problemsNotice" : "workflows.edit.reviewNotice")}</p>}
        />
      </div>
      {drawer === null ? null : (
        <ModalShell title={drawer.base === null ? t("authoring.step.newTitle") : t("authoring.step.editPageTitle", { name: translatedText(drawer.base.definition.translations, formLanguages(i18n.resolvedLanguage)[0], { field: "name" }) ?? drawer.base.definition.name })} onClose={() => { drawerCloseRef.current?.requestClose(() => { setDrawer(null); }); }} size="6xl">
          {/* The step goes into the Workflow's change set; only the Workflow's own save answers an edit step (ADR-0031 D2), and copies the Workflows on the way. */}
          <ChangeSetStepContext.Provider value={COMMIT_ONLY}>
            <DerivationRootContext.Provider value={null}>
              <StepForm projectId={projectId} options={options} base={drawer.base} closeRef={drawerCloseRef} onAdded={stepAdded} changeSetId={sessionChangeSetId} />
            </DerivationRootContext.Provider>
          </ChangeSetStepContext.Provider>
        </ModalShell>
      )}
    </div>
  );
}
